import React, { useState } from 'react';
import { useAppSelector } from '../../../../hooks/use-app-selector';
import { useLocalStorageState } from 'ahooks';
import { useNavigate } from 'react-router-dom';
import { SubscriptionUpgradeCard } from './SubscriptionUpgradeCard';
import { SetupProgressCard } from './SetupProgressCard';
import {
  isWhitelabelAccount,
  isResellerAccount,
} from '../../../../shared/utils';
import { getAppSettings } from '../../../../selectors';
import { useGlobals } from '../../../../globals-context';
import { BETA_TOGGLES } from '../../../../features/beta/screens/Beta';
import { SetupProgressModal } from './SetupProgressModal';
import { useFetchOnboardingSteps } from '@shared/hooks/onboardings';

type SidebarActionCardsProps = {
  sideMenuCollapsed: boolean;
};

export function SidebarActionCards({
  sideMenuCollapsed,
}: SidebarActionCardsProps) {
  const navigate = useNavigate();
  const [openSetupProgressModal, setOpenSetupProgressModal] = useState(false);
  const { trackMethod } = useGlobals() as any;

  const { data: onboardingStepsData, isLoading: isLoadingOnboardingSteps } =
    useFetchOnboardingSteps();

  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const { subscriptionPartner } =
    useAppSelector((state) => getAppSettings(state)) || {};

  const packageCode = useAppSelector(
    (state) => state.currentMerchant.packageCode
  );
  const isWhitelabel = isWhitelabelAccount(currentMerchant.whiteLabel);
  const isTrialMerchant = (currentMerchant?.trialDaysLeft || 0) > 0;
  const isReseller = isResellerAccount(subscriptionPartner);

  const showSubscriptionUpgradeCard =
    !isWhitelabel &&
    !isReseller &&
    (isTrialMerchant || packageCode !== 'shore_pos_premium');

  const showSetupProgressCard =
    (onboardingStepsData?.overall_progress_percentage ?? 100) < 100;

  const [showBetaSubscriptionUpgradeCard] = useLocalStorageState<boolean>(
    BETA_TOGGLES.subscriptionUpgradeCard.key,
    {
      listenStorageChange: true,
    }
  );

  const [showBetaSetupProgressBanner] = useLocalStorageState<boolean>(
    BETA_TOGGLES.setupProgressBanner.key,
    {
      listenStorageChange: true,
    }
  );

  const handleOnSubscriptionUpgradeCardClick = () => {
    trackMethod('subscription_upgrade_card_clicked', {
      isTrialMerchant,
      trialDaysLeft: currentMerchant?.trialDaysLeft || 0,
    });
    if (isTrialMerchant) {
      navigate('/subscription');
    } else {
      navigate('/subscription/upgrade');
    }
  };

  const handleOnSetupProgressCardClick = () => {
    trackMethod('setup_progress_card_clicked');
    setOpenSetupProgressModal(true);
  };

  return (
    <>
      {showBetaSubscriptionUpgradeCard && showSubscriptionUpgradeCard && (
        <SubscriptionUpgradeCard
          sideMenuCollapsed={sideMenuCollapsed}
          onCardClick={handleOnSubscriptionUpgradeCardClick}
        />
      )}
      {showBetaSetupProgressBanner && showSetupProgressCard && (
        <SetupProgressCard
          sideMenuCollapsed={sideMenuCollapsed}
          onboardingStepsData={onboardingStepsData}
          onCardClick={handleOnSetupProgressCardClick}
        />
      )}
      <SetupProgressModal
        openModal={openSetupProgressModal}
        onCloseModalClick={() => setOpenSetupProgressModal(false)}
        onboardingStepsData={onboardingStepsData}
        isLoadingOnboardingSteps={isLoadingOnboardingSteps}
      />
    </>
  );
}
